/* @flow */

import * as React from 'react';
import get from 'lodash/get';
import {reduxForm, Field} from 'redux-form';
import type {FormProps} from 'redux-form/lib/types.js.flow';
import type {Task as TaskFragment} from 'nutshell-graphql-types';
import type {Assignable} from 'nutshell-core/types';
import {TickCircleIcon} from 'shells/icon';
import type {FlashMessage} from '../../creator/types';
import {fetchOptions} from '../fetch-options';
import {SelectPicker} from '../fields/select-picker';
import {FormWrapper} from '../form-wrapper/form-wrapper';
import {TextField} from '../fields/text-field';
import {DateTime} from '../fields/date-time';
import {DefaultFooter} from '../default-footer/default-footer';
import {TaskFooter} from '../footers/task-footer';

import '../form-common.css';

type Props = {
    // This will only exist when viewing the task page tasks/{slug}
    task: ?TaskFragment,
    isReadOnly: boolean,
    onCancelClick: () => any,
    onUpdateTask: () => any,

    handleSubmit: $PropertyType<FormProps, 'handleSubmit'>,
    reset: $PropertyType<FormProps, 'reset'>,
    submitting: $PropertyType<FormProps, 'submitting'>,
    submitFailed: $PropertyType<FormProps, 'submitFailed'>,

    usersAndTeams: Assignable[],
    pushFlashMessage: (FlashMessage) => void,

    // `form` is used by redux-form
    form: string, // eslint-disable-line react/no-unused-prop-types
};

export class TaskFormConnectedComponent extends React.PureComponent<Props> {
    render() {
        return (
            <form styleName='form' onSubmit={this.props.handleSubmit}>
                <FormWrapper
                    icon={TickCircleIcon}
                    footer={this.renderFooter()}
                    isFullHeight={Boolean(this.props.task)}
                >
                    <Field
                        name='title'
                        label='Name'
                        disabled={this.props.isReadOnly}
                        component={TextField}
                        required={true}
                        autoFocus={!NutshellFrontendSettings.isDemoSilo}
                    />
                    <div>
                        {this.shouldIncludeField('dueTime') ? (
                            <Field
                                name='dueTime'
                                label='Due date'
                                disabled={this.props.isReadOnly}
                                component={DateTime}
                            />
                        ) : null}
                    </div>
                    <div>
                        {this.shouldIncludeField('links.assignee') ? (
                            <Field
                                name='links.assignee'
                                label='Assignee'
                                disabled={this.props.isReadOnly}
                                component={SelectPicker}
                                clearable={false}
                                labelKey='name'
                                options={this.props.usersAndTeams}
                                valueKey='id'
                            />
                        ) : null}
                    </div>
                    <div>
                        {this.shouldIncludeField('links.relatedEntity') ? (
                            <Field
                                name='links.relatedEntity'
                                label='Related to'
                                disabled={this.props.isReadOnly}
                                labelKey='name'
                                valueKey='id'
                                clearable={true}
                                allowCreate={true}
                                creatableCollections={['accounts', 'contacts']}
                                loadOptions={fetchOptions(['accounts', 'contacts', 'leads'])}
                                onCreate={this.handleSelectPickerOnCreate}
                                component={SelectPicker}
                            />
                        ) : null}
                    </div>
                    <div>
                        {this.shouldIncludeField('description') ? (
                            <Field
                                name='description'
                                label='Notes'
                                disabled={this.props.isReadOnly}
                                multiline={true}
                                component={TextField}
                            />
                        ) : null}
                    </div>
                </FormWrapper>
            </form>
        );
    }

    renderFooter = () => {
        // If editing an existing task
        if (this.props.task) {
            return this.props.isReadOnly ? (
                <TaskFooter
                    task={this.props.task}
                    onToggleTaskCompletion={this.handleToggleTaskCompletion}
                    onUpdateTask={this.props.onUpdateTask}
                />
            ) : (
                // Form footer with default save button
                <DefaultFooter
                    onCancel={this.handleCancelClick}
                    isSaving={this.props.submitting}
                    errorSaving={this.props.submitFailed}
                />
            );
        }

        return (
            // Form footer when creating a new task
            <DefaultFooter
                onCancel={this.handleCancelClick}
                isSaving={this.props.submitting}
                errorSaving={this.props.submitFailed}
                buttonText='Create task'
                savingText='Creating task…'
            />
        );
    };

    handleCancelClick = () => {
        this.props.onCancelClick();
        this.props.reset();
    };

    handleToggleTaskCompletion = (isCompleting: boolean) => {
        this.props.pushFlashMessage({
            message: `This task was ${isCompleting ? 'completed' : 'updated'} successfully.`,
        });

        // Callback to refetch our task when updated
        this.props.onUpdateTask();
    };

    handleSelectPickerOnCreate = (options: {value: string, entityType: string, type?: 'error'}) => {
        if (options.type === 'error') {
            this.props.pushFlashMessage({
                message: `Something went wrong trying to create a ${options.entityType} "${options.value}". Please try again.`,
                duration: undefined,
                type: 'error',
            });
        } else {
            this.props.pushFlashMessage({
                message: `A new ${options.entityType} "${options.value}" was successfully created.`,
            });
        }
    };

    shouldIncludeField(fieldName: string) {
        // If we're in edit mode, always include the field
        if (!this.props.isReadOnly) return true;

        // If we somehow opened the form in readOnly without a task, hide everything.  :(
        if (!this.props.task) return false;

        if (fieldName === 'links.assignee') {
            return this.props.task.assignee;
        }

        if (fieldName === 'links.relatedEntity') {
            return this.props.task.relatedEntity;
        }

        // If read only mode, we only want to show non-empty fields
        return get(this.props.task, fieldName);
    }
}

export const TaskFormConnected = reduxForm()(TaskFormConnectedComponent);
